import {
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type {
  MarkAllNotificationInboxReadResponse,
  NotificationInboxClassTarget,
  NotificationInboxItem,
  NotificationInboxItemDto,
  NotificationInboxTarget,
  NotificationInboxUnreadCountDto,
  NotificationInboxViewer,
  NotificationKind,
  SendAudienceNotificationRequest,
  SendNotificationResponse,
} from "@membentry/shared-types";
import type { AxiosInstance } from "axios";

export const NOTIFICATION_INBOX_QUERY_KEY = "notification-inbox";

export interface SendNotificationToUserInput {
  userId: string;
  title: string;
  body: string;
  data?: Record<string, string>;
}

export interface SendNotificationToClubInput {
  clubId: number;
  title: string;
  body: string;
  data?: Record<string, string>;
  userIds?: string[];
}

export interface NotificationHooksTransport {
  sendNotificationToUser: (
    input: SendNotificationToUserInput,
  ) => Promise<SendNotificationResponse>;
  sendNotificationToClub: (
    input: SendNotificationToClubInput,
  ) => Promise<SendNotificationResponse>;
  sendNotificationToAudience: (
    input: SendAudienceNotificationRequest,
  ) => Promise<SendNotificationResponse>;
}

export interface NotificationInboxOptions {
  page?: number;
  pageSize?: number;
  unreadOnly?: boolean;
  /** Called when an inbox row is dropped due to missing target metadata or unknown kind. */
  onMalformedItem?: (item: NotificationInboxItemDto, reason?: string) => void;
}

function mapBackendKind(kind: string): NotificationKind | null {
  switch (kind) {
    case "membership_request_approved":
      return "membership_request_approved";
    case "membership_request_rejected":
      return "membership_request_rejected";
    case "membership_request_created":
      return "membership_request_created";
    case "membership_request_cancelled":
      return "membership_request_cancelled";
    case "recurring_enrollment_ended":
      return "recurring_enrollment_ended";
    case "class_trainer_signup":
      return "class_trainer_signup";
    case "class_trainer_booking_cancelled":
      return "class_trainer_booking_cancelled";
    case "class_member_booking_cancelled":
      return "class_member_booking_cancelled";
    case "class_occurrence_updated":
      return "class_occurrence_updated";
    case "class_occurrence_cancelled":
      return "class_occurrence_cancelled";
    case "class_series_cancelled_member":
      return "class_series_cancelled_member";
    case "class_series_cancelled_trainer":
      return "class_series_cancelled_trainer";
    default:
      return null;
  }
}

function parseNotificationInboxData(
  input: string | null | undefined,
): Record<string, unknown> | null {
  if (!input) {
    return null;
  }

  try {
    const parsed = JSON.parse(input) as unknown;
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function deriveViewer(kind: string): NotificationInboxViewer {
  if (
    kind === "membership_request_created" ||
    kind === "membership_request_cancelled"
  ) {
    return "owner";
  }

  if (
    kind === "class_trainer_signup" ||
    kind === "class_trainer_booking_cancelled" ||
    kind === "class_series_cancelled_trainer"
  ) {
    return "trainer";
  }

  return "member";
}

function deriveClassViewerContext(kind: string): NotificationInboxClassTarget["viewerContext"] {
  return kind === "class_trainer_signup" || kind === "class_series_cancelled_trainer"
    || kind === "class_trainer_booking_cancelled"
    ? "trainer"
    : "member";
}

function deriveTarget(
  item: NotificationInboxItemDto,
  viewer: NotificationInboxViewer,
  startsAt?: string,
): NotificationInboxTarget | null {
  const viewerContext = deriveClassViewerContext(item.kind);

  if (item.seriesId) {
    if (!item.clubId) {
      return null;
    }

    return {
      type: "class-series",
      viewer,
      viewerContext,
      clubId: item.clubId,
      seriesId: item.seriesId,
    };
  }

  if (item.classId) {
    if (!item.clubId) {
      return null;
    }

    return {
      type: "class",
      viewer,
      viewerContext,
      clubId: item.clubId,
      classId: item.classId,
      startsAt,
    };
  }

  if (!item.clubId || !item.membershipRequestId) {
    return null;
  }

  return {
    type: "membership-request",
    viewer,
    clubId: item.clubId,
    membershipRequestId: item.membershipRequestId,
  };
}

function parseTargetId(value: unknown): number | null {
  // Backend serializes ids into the data JSON as strings
  // ("{\"membershipRequestId\":\"44\"}"), so accept both.
  const parsed = typeof value === "string" ? Number(value) : value;
  return typeof parsed === "number" && Number.isFinite(parsed) && parsed > 0
    ? parsed
    : null;
}

function mapNotificationInboxItem(
  item: NotificationInboxItemDto,
  onMalformedItem?: (item: NotificationInboxItemDto, reason?: string) => void,
): NotificationInboxItem | null {
  const parsedData = parseNotificationInboxData(item.data);
  const viewer = deriveViewer(item.kind);

  const clubId = parseTargetId(parsedData?.clubId) ?? item.clubId ?? null;
  const membershipRequestId =
    parseTargetId(parsedData?.membershipRequestId) ??
    item.membershipRequestId ??
    null;
  const classId = parseTargetId(parsedData?.classId) ?? item.classId ?? null;
  const seriesId = parseTargetId(parsedData?.seriesId) ?? item.seriesId ?? null;
  const startsAt =
    typeof parsedData?.startsAt === "string" ? parsedData.startsAt : undefined;

  const mappedKind = mapBackendKind(item.kind);
  if (!mappedKind) {
    onMalformedItem?.(item, "unknown-kind");
    return null;
  }

  const target = deriveTarget(
    { ...item, clubId, membershipRequestId, classId, seriesId },
    viewer,
    startsAt,
  );

  // An item without a resolvable target cannot be opened — drop it instead
  // of throwing, so one malformed row does not fail the whole inbox query.
  if (!target) {
    console.warn(
      "[notification-inbox] Dropping malformed row — missing target metadata",
      {
        id: item.id,
        kind: item.kind,
        clubId: item.clubId,
        membershipRequestId: item.membershipRequestId,
        classId: item.classId,
        seriesId: item.seriesId,
      },
    );
    onMalformedItem?.(item);
    return null;
  }

  return {
    id: item.id,
    kind: mappedKind,
    occurredAt: item.createdAt,
    createdAt: item.createdAt,
    readAt: item.readAt ?? null,
    isRead: item.isRead,
    clubId,
    membershipRequestId,
    classId,
    seriesId,
    actorUserId: item.actorUserId ?? null,
    actorName: item.actorName ?? null,
    actorRole: item.actorRole ?? null,
    title: item.title,
    body: item.body,
    rawData: item.data ?? null,
    target,
  };
}

function sortNotificationInbox(items: NotificationInboxItem[]): NotificationInboxItem[] {
  return [...items].sort(
    (left, right) =>
      new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime(),
  );
}

export function useNotificationInboxQuery(
  client: AxiosInstance,
  viewer: NotificationInboxViewer,
  options: NotificationInboxOptions = {},
  queryOptions?: Omit<UseQueryOptions<NotificationInboxItem[]>, "queryKey" | "queryFn">,
) {
  const { page = 0, pageSize = 20, unreadOnly = false, onMalformedItem } = options;

  return useQuery({
    queryKey: [NOTIFICATION_INBOX_QUERY_KEY, viewer, page, pageSize, unreadOnly],
    queryFn: async () => {
      const { getNotificationInbox } = await import("@membentry/shared-api");
      const response = await getNotificationInbox(client, { page, pageSize, unreadOnly });
      const items = Array.isArray(response.payload) ? response.payload : [];
      const expectedViewer = viewer;

      return sortNotificationInbox(
        items
          .map((item) => mapNotificationInboxItem(item, onMalformedItem))
          .filter(
            (item): item is NotificationInboxItem =>
              item !== null && item.target.viewer === expectedViewer,
          ),
      );
    },
    ...queryOptions,
  });
}

export function useMemberNotificationInboxQuery(
  client: AxiosInstance,
  options: NotificationInboxOptions = {},
  queryOptions?: Omit<UseQueryOptions<NotificationInboxItem[]>, "queryKey" | "queryFn">,
) {
  return useNotificationInboxQuery(client, "member", options, queryOptions);
}

export function useOwnerNotificationInboxQuery(
  client: AxiosInstance,
  options: NotificationInboxOptions = {},
  queryOptions?: Omit<UseQueryOptions<NotificationInboxItem[]>, "queryKey" | "queryFn">,
) {
  return useNotificationInboxQuery(client, "owner", options, queryOptions);
}

export function useTrainerNotificationInboxQuery(
  client: AxiosInstance,
  options: NotificationInboxOptions = {},
  queryOptions?: Omit<UseQueryOptions<NotificationInboxItem[]>, "queryKey" | "queryFn">,
) {
  return useNotificationInboxQuery(client, "trainer", options, queryOptions);
}

export function useNotificationInboxUnreadCountQuery(
  client: AxiosInstance,
  queryOptions?: Omit<UseQueryOptions<NotificationInboxUnreadCountDto>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: [NOTIFICATION_INBOX_QUERY_KEY, "unread-count"],
    queryFn: async () => {
      const { getNotificationInboxUnreadCount } = await import("@membentry/shared-api");
      return getNotificationInboxUnreadCount(client);
    },
    ...queryOptions,
  });
}

export function invalidateNotificationInbox(
  queryClient: { invalidateQueries: (input: { queryKey: unknown[] }) => void },
  viewer?: NotificationInboxViewer,
) {
  if (!viewer) {
    queryClient.invalidateQueries({ queryKey: [NOTIFICATION_INBOX_QUERY_KEY] });
    queryClient.invalidateQueries({
      queryKey: [NOTIFICATION_INBOX_QUERY_KEY, "unread-count"],
    });
    return;
  }

  queryClient.invalidateQueries({
    queryKey: [NOTIFICATION_INBOX_QUERY_KEY, viewer],
  });
  queryClient.invalidateQueries({
    queryKey: [NOTIFICATION_INBOX_QUERY_KEY, "unread-count"],
  });
}

export function useMarkNotificationInboxItemReadMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<NotificationInboxItem | null, Error, number>,
    "mutationFn"
  >,
) {
  return useMutation({
    mutationFn: async (notificationId: number) => {
      const { markNotificationInboxItemRead } = await import("@membentry/shared-api");
      const dto = await markNotificationInboxItemRead(client, notificationId);
      return mapNotificationInboxItem(dto);
    },
    ...options,
  });
}

export function useMarkAllNotificationInboxItemsReadMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<MarkAllNotificationInboxReadResponse, Error, void>,
    "mutationFn"
  >,
) {
  return useMutation({
    mutationFn: async () => {
      const { markAllNotificationInboxItemsRead } = await import("@membentry/shared-api");
      return markAllNotificationInboxItemsRead(client);
    },
    ...options,
  });
}

export function useSendNotificationToUserMutation(
  transport: NotificationHooksTransport,
  options?: Omit<
    UseMutationOptions<SendNotificationResponse, Error, SendNotificationToUserInput>,
    "mutationFn"
  >,
) {
  return useMutation({ mutationFn: transport.sendNotificationToUser, ...options });
}

export function useSendNotificationToClubMutation(
  transport: NotificationHooksTransport,
  options?: Omit<
    UseMutationOptions<SendNotificationResponse, Error, SendNotificationToClubInput>,
    "mutationFn"
  >,
) {
  return useMutation({ mutationFn: transport.sendNotificationToClub, ...options });
}

export function useSendNotificationToAudienceMutation(
  transport: NotificationHooksTransport,
  options?: Omit<
    UseMutationOptions<
      SendNotificationResponse,
      Error,
      SendAudienceNotificationRequest
    >,
    "mutationFn"
  >,
) {
  return useMutation({
    mutationFn: transport.sendNotificationToAudience,
    ...options,
  });
}
